import { useEffect, useRef, useState } from "react";
import { Wrench } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useHasPermission } from "@/auth/access-permissions";
import { Button } from "@/components/design/Button";
import { ServiceCard } from "@/pages/save/service/ServiceCard";
import type { Comparable, ComparableIdentity } from "./comparable";

export function ComparisonTools<T extends ComparableIdentity>({ comparable, current, viewerId }: { comparable: Comparable<T>; current: T; viewerId: string }) {
  const allowed = useHasPermission(comparable.permission);
  const intl = useIntl();
  const selection = comparable.useSelection();
  const items = selection.viewerId === viewerId ? selection.items : [];
  const selected = items.some((p) => p.id === current.id);
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !wrapper.current?.contains(event.target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") { setOpen(false); trigger.current?.focus(); }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  if (!allowed) return null;
  return (
    <div ref={wrapper} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
        <Button id={`${comparable.kind}-tools`} ref={trigger} variant="secondary" size="square" aria-label={intl.formatMessage({ id: comparable.messages.tools })} aria-expanded={open} aria-controls={`${comparable.kind}-tools-panel`} onClick={() => setOpen(true)}><Wrench size={18} aria-hidden="true" /></Button>
        {open && <div id={`${comparable.kind}-tools-panel`} className="toggle-pop absolute left-0 top-full z-[var(--z-dropdown)] max-h-[80vh] w-[min(92vw,420px)] origin-top-left overflow-auto rounded-md bg-surface p-4 shadow-lg ring-1 ring-ink/10">
          <div className="smycles-field-label mb-2"><FormattedMessage id={comparable.messages.tools} /></div>
          <div className="grid grid-cols-2 gap-3">
          <ServiceCard id={`${comparable.kind}-comparison-select`} icon="◫"
            title={intl.formatMessage({ id: selected ? comparable.messages.remove : comparable.messages.select })}
            desc={intl.formatMessage({ id: selected ? comparable.messages.removeDescription : comparable.messages.selectDescription })}
            tags={intl.formatMessage({ id: comparable.messages.count }, { count: items.length })}
            disabled={!selected && items.length === 2} onClick={() => {
            if (selected) selection.remove(current.id); else selection.select(viewerId, current);
            setOpen(false); trigger.current?.focus();
          }} />
          </div>
          {!selected && items.length === 2 && <p className="smycles-field-meta mt-3"><FormattedMessage id={comparable.messages.full} /></p>}
        </div>}
    </div>
  );
}
